import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaRegularIcon, FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {
    swal,
    swalConfirm,
    swalError,
    swalLoading,
    swalSuccess
} from "../../../../../helpers/SweetAlertWrapper";
import {BookizonAppManager} from "../../../../../index";
import NativeGeolocationProvider from "../../../../../providers/NativeGeolocationProvider";
import PulsatingCircle from "../../../../../components/PulsatingCircle";

export default class ClockingSessionsView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedStaffId: this.getDefaultStaffId(props.staffList),
            loading: true,
            error: null,
            presenceData: null,
            submitting: false,
            locating: false,
            pendingAction: null
        };
    }

    componentDidMount() {
        this.loadPresenceData();
    }

    componentWillUnmount() {
        this.geolocationDisposed = true;
        NativeGeolocationProvider.cancelForOwner(this);
        swal.safeClose();
    }

    getDefaultStaffId = staffList => {
        if (!staffList || !staffList.length) return null;
        return staffList[0].id_dipendente || null;
    }

    loadPresenceData = (showLoading = true) => {
        if (!this.state.selectedStaffId) {
            this.setState({loading: false, error: "Nessun dipendente collegato a questo utente"});
            return Promise.resolve();
        }
        if (showLoading) this.setState({loading: true, error: null});

        return ModuloHRAPI.ClockingSessions.presenceData(this.state.selectedStaffId)
            .then(this.handlePresenceDataLoaded)
            .catch(this.handlePresenceDataError);
    }

    handlePresenceDataLoaded = presenceData => {
        this.setState({
            loading: false,
            error: null,
            presenceData: presenceData || null
        });
    }

    handlePresenceDataError = error => {
        this.setState({
            loading: false,
            error: error || "Non è stato possibile recuperare le timbrature"
        });
    }

    handleRetryClick = _ => this.loadPresenceData();

    handleStaffChange = event => {
        const selectedStaffId = parseInt(event.target.value, 10) || null;
        this.setState({selectedStaffId: selectedStaffId, presenceData: null}, this.loadPresenceData);
    }

    handleStartClick = _ => this.prepareClockingAction("start");

    handleStopClick = event => this.prepareClockingAction("stop");

    hasRequiredGeolocation = () => {
        const settings = this.state.presenceData?.settings || {};
        return settings.require_geolocation === true || parseInt(settings.require_geolocation || 0, 10) === 1;
    }

    prepareClockingAction = action => {
        if (this.state.submitting || this.state.locating) return;
        if (!this.hasRequiredGeolocation()) {
            this.submitClocking(action, null);
            return;
        }

        this.setState({pendingAction: action, locating: true});
        swalLoading("Recupero posizione...");

        NativeGeolocationProvider.getCurrentLocation('ClockingSessionsPage', false, true, {owner: this, maximumAgeMs: 10000})
            .then(this.handleLocationSuccess)
            .catch(this.handleLocationError);
    }

    handleLocationSuccess = location => {
        if (this.geolocationDisposed) return;
        const clockingPosition = {
            lat: location.latitude,
            lon: location.longitude
        };
        const pendingAction = this.state.pendingAction;
        this.setState({locating: false});
        swal.safeClose();
        swalConfirm(
            <Fragment>
                <div>Vuoi confermare la seguente posizione per questa timbratura?</div>
                <a
                    href={this.getPositionMapLink(clockingPosition)}
                    aria-label={"Apri la posizione nel navigatore"}
                >
                    <img
                        className={"img-fluid rounded border mt-3"}
                        src={this.getPositionMapImageUrl(clockingPosition)}
                        alt={"Posizione rilevata sulla mappa"}
                    />
                </a>
                <div className={"small text-muted mt-2"}>
                    Latitudine {clockingPosition.lat}<br/>Longitudine {clockingPosition.lon}
                </div>
            </Fragment>,
            "Conferma",
            "Annulla"
        )
            .then(_ => this.submitClocking(pendingAction, clockingPosition))
            .catch(this.handleLocationDismiss);
    }

    handleLocationError = error => {
        if (this.geolocationDisposed) return;
        const pendingAction = this.state.pendingAction;
        this.setState({locating: false});
        swal.safeClose();
        swalConfirm(
            "Non è stato possibile recuperare la posizione del dispositivo. Vuoi proseguire comunque?",
            "Prosegui",
            "Annulla"
        )
            .then(_ => this.submitClocking(pendingAction, {lat: -1, lon: -1}))
            .catch(this.handleLocationDismiss);
    }

    handleLocationDismiss = _ => {
        this.setState({pendingAction: null, locating: false});
    }

    submitClocking = (action, position) => {
        if (!action || !this.state.selectedStaffId) return;

        const request = action === "start"
            ? ModuloHRAPI.ClockingSessions.startPresence
            : ModuloHRAPI.ClockingSessions.stopPresence;

        this.setState({submitting: true, pendingAction: action});
        swalLoading(action === "start" ? "Registrazione ingresso..." : "Registrazione uscita...");
        request(this.state.selectedStaffId, position)
            .then(response => {
                this.loadPresenceData(false);
                swalSuccess(response?.message || "Timbratura registrata");
            })
            .catch(e => swalError(e || "Non è stato possibile registrare la timbratura"))
            .finally(_ => this.setState({submitting: false, pendingAction: null}));
    }

    getSelectedStaff = () => {
        return (this.props.staffList || []).find(staff => {
            return parseInt(staff.id_dipendente, 10) === parseInt(this.state.selectedStaffId, 10);
        }) || null;
    }

    getDateLabel = date => date ? moment(date, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";

    getPositionMapImageUrl = position => {
        const coordinates = `${position.lat},${position.lon}`;
        const encodedCoordinates = encodeURIComponent(coordinates);
        return "https://maps.googleapis.com/maps/api/staticmap" +
            "?key=AIzaSyAsRTtIb0cMy_bpR5RbavHoNKvIkGPlHpg" +
            "&size=400x400" +
            `&center=${encodedCoordinates}` +
            "&zoom=18" +
            `&markers=color:red%7C${encodedCoordinates}`;
    }

    getPositionMapLink = position => {
        const coordinates = `${position.lat},${position.lon}`;
        const isIOS = BookizonAppManager.getMobileOperatingSystem() === 'iOS';
        return isIOS
            ? `http://maps.apple.com/?q=${coordinates}`
            : `https://maps.google.com/maps?q=${coordinates}`;
    }

    getSessionStartTime = session => session.start_time_corrected || session.start_time;

    getSessionEndTime = session => session.end_time_corrected || session.end_time;

    getWorkedHours = session => {
        const startTime = this.getSessionStartTime(session);
        const endTime = this.getSessionEndTime(session);
        const start = session.session_date && startTime
            ? moment(`${session.session_date} ${startTime}`, "YYYY-MM-DD HH:mm")
            : null;
        const end = endTime
            ? moment(`${session.session_date} ${endTime}`, "YYYY-MM-DD HH:mm")
            : moment();

        if (!start || !start.isValid() || !end.isValid() || end.isBefore(start)) return "-";

        const minutes = end.diff(start, "minutes");
        return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
    }

    renderStaffSelector = () => {
        if ((this.props.staffList || []).length <= 1) return null;

        return (
            <div className={"form-group"}>
                <label htmlFor={"clocking-staff-member"}>Rapporto di lavoro</label>
                <select
                    id={"clocking-staff-member"}
                    className={"form-control"}
                    value={this.state.selectedStaffId || ""}
                    onChange={this.handleStaffChange}
                    disabled={this.state.loading || this.state.submitting || this.state.locating}
                >
                    {
                        this.props.staffList.map(staff => {
                            const label = staff.negozio?.nome
                                ? `${staff.nome} - ${staff.negozio.nome}`
                                : staff.nome;
                            return (
                                <option key={staff.id_dipendente} value={staff.id_dipendente}>
                                    {label}
                                </option>
                            );
                        })
                    }
                </select>
            </div>
        );
    }
    renderStartBox = (sessions) => {
        if (!this.state.presenceData?.can_start) return null;
        const expectedRanges = this.state.presenceData.expected_ranges || [];
        const nextExpectedRange = expectedRanges[sessions.length] || null;

        return (
            <div className={"mb-3"}>
                <button
                    type={"button"}
                    className={"btn btn-success btn-block mt-3 btn-sm"}
                    onClick={this.handleStartClick}
                    disabled={this.state.submitting || this.state.locating}
                >
                    <FaSolidIcon
                        name={"sign-in-alt"}/> {sessions.length ? "Timbra altro ingresso per oggi" : "Timbra ingresso per oggi"}
                </button>
                {
                    !!(nextExpectedRange && nextExpectedRange.start) &&
                    <div className={"small text-muted text-center mt-1"}>
                        Ingresso previsto {nextExpectedRange.start}
                    </div>
                }
            </div>
        );
    }

    renderSessionAction = (expectedRange) => {
        return (
            <Fragment>
                {
                    this.state.presenceData?.can_stop &&
                    <button
                        type={"button"}
                        className={"btn btn-warning btn-block btn-sm"}
                        onClick={this.handleStopClick}
                        disabled={this.state.submitting || this.state.locating}
                    >
                        <FaSolidIcon name={"sign-out-alt"}/> Timbra uscita
                    </button>
                }
                {
                    !this.state.presenceData?.can_stop &&
                    <div className={"small text-muted text-center"}>
                        Questa sessione deve essere chiusa con lo stesso metodo usato per l'ingresso.
                    </div>
                }
                {
                    !!(expectedRange && expectedRange.end) &&
                    <div className={"small text-muted text-center mt-1"}>
                        Uscita prevista {expectedRange.end}
                    </div>
                }
            </Fragment>
        );
    }

    renderSession = (session, index, sessions, expectedRanges, itemCount) => {
        const endTime = this.getSessionEndTime(session);
        const isOpen = !endTime;
        const expectedRange = expectedRanges[sessions.length - 1 - index] || null;

        return (
            <div className={`list-group-item`}
                 key={session.session_id || index}>
                <div className={"d-flex align-items-center justify-content-between"}>
                    <div className={"mr-3"}>
                        <div className={"small text-muted"}>{isOpen ? "Ingresso" : "Ingresso - Uscita"}</div>
                        <div>
                            <FaRegularIcon name={"clock"}/> {this.getSessionStartTime(session) || "-"}
                            {!isOpen && ` - ${endTime || "-"}`}
                        </div>
                        {
                            (!isOpen && !!expectedRange) &&
                            <div className={"small text-muted text-center mt-1"}>
                                Previsto {expectedRange.start || "-"} - {expectedRange.end || "-"}
                            </div>
                        }
                    </div>
                    <div>
                        <div className={"small text-muted"}>Ore lavorate</div>
                        <div>

                            {
                                isOpen ?
                                    <PulsatingCircle
                                        backgroundColor="#28a745"
                                        pulseColor="#28a745"
                                        className={"mr-1"}
                                        size={16}
                                        pulseRadius={8}
                                        duration={1.4}
                                    />
                                    :
                                    <FaSolidIcon name={"circle"} className={"text-muted mr-1"}/>
                            }

                            {this.getWorkedHours(session)}
                        </div>
                    </div>
                </div>
                {isOpen && <div className={"mt-2"}>
                    {this.renderSessionAction(expectedRange)}
                </div>}
            </div>
        );
    }

    renderPresenceContent = () => {
        if (this.state.loading) {
            return <div className={"py-5 text-center text-muted"}>Caricamento timbrature...</div>;
        }
        if (this.state.error) {
            return (
                <div className={"alert alert-danger text-center"}>
                    <div>{this.state.error}</div>
                    <button type={"button"} className={"btn btn-outline-danger mt-3"} onClick={this.handleRetryClick}>
                        <FaSolidIcon name={"sync-alt"}/> Riprova
                    </button>
                </div>
            );
        }
        if (!this.state.presenceData) return null;

        const sessions = this.state.presenceData.sessions || [];
        const expectedRanges = this.state.presenceData.expected_ranges || [];
        const enabledMethods = this.state.presenceData.settings?.enabled_methods || [];
        const mobileAppEnabled = enabledMethods.indexOf("mobile_app") !== -1;

        return (
            <Fragment>
                {
                    !mobileAppEnabled &&
                    <div className={"alert alert-warning"}>
                        La timbratura dall'app non è abilitata per questo dipendente.
                    </div>
                }
                {this.renderStartBox(sessions)}
                <div className={"list-group"}>
                    {
                        sessions.map((session, index) => {
                            return this.renderSession(session, index, sessions, expectedRanges, sessions.length);
                        })
                    }
                </div>
            </Fragment>
        );
    }

    render() {
        if (this.state.loading) return '';
        if ((this.state.presenceData?.settings?.enabled_methods || []).indexOf("mobile_app") === -1) return '';

        const date = this.state.presenceData?.date;

        return (
            <div className={"container px-3 py-3 border-top mt-2"}>
                <div className={"small text-muted mb-2"}>
                    <FaSolidIcon name={"id-card"}/> Timbrature di oggi {this.getDateLabel(date)}
                </div>
                {this.renderStaffSelector()}
                {this.renderPresenceContent()}
            </div>
        );
    }
}

ClockingSessionsView.propTypes = {
    staffList: PropTypes.arrayOf(PropTypes.shape({
        id_dipendente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        nome: PropTypes.string,
        negozio: PropTypes.shape({
            nome: PropTypes.string
        })
    }))
};

ClockingSessionsView.defaultProps = {
    staffList: []
};

